import React, {
    useState, useCallback, useEffect, useLayoutEffect, useRef
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Skeleton from '@mui/material/Skeleton';
import { getLoaderStatus } from '../loader/loaderSelector';
import { isAnimatedImage } from '../utils/urlUtils';
import { useGifPlayback } from './GifPlaybackContext';
import { isPlayAllAnimationsFeatureEnabled } from './gifPlaybackUtils';
import { captureGifFreezeFrame, measureImgBox, useDebouncedResizeListener } from './gifFreezeFrameUtils';
import GifTag from './GifTag';




const ImageWithSkeleton = ({
    src,
    alt,
    className = '',
    skeletonClassName = '',
    onLoad: onLoadProp,
    style,
    skeletonSx,
    fetchPriority,
    isAnimated
}) => {
    const isLoading = useSelector(getLoaderStatus);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const imgRef = useRef(null);
    const canvasRef = useRef(null);
    const isGif = isAnimatedImage(isAnimated) && isPlayAllAnimationsFeatureEnabled();
    const { isPlaying, registerGif, unregisterGif } = useGifPlayback();
    // Mirrors the <img>'s rendered box so the frozen canvas aligns exactly.
    const [canvasSize, setCanvasSize] = useState(null);

    const handleLoad = useCallback(() => {
        setLoaded(true);
        if (onLoadProp) onLoadProp();
    }, [onLoadProp]);

    const handleError = useCallback(() => {
        setError(true);
    }, []);

    const showSkeleton = isLoading || (!loaded && !error);

    
    useEffect(() => {
        if (!isGif) return undefined;
        registerGif();
        return () => unregisterGif();
    }, [isGif, registerGif, unregisterGif]);

    
    
    
    useLayoutEffect(() => {
        if (!isGif || !loaded || showSkeleton || isPlaying) return;
        const size = captureGifFreezeFrame(imgRef.current, canvasRef.current);
        if (size) setCanvasSize(size);
    }, [isGif, loaded, showSkeleton, isPlaying]);

    
    useDebouncedResizeListener(() => {
        const img = imgRef.current;
        if (img) setCanvasSize(measureImgBox(img));
    }, isGif && loaded && !isPlaying);

    const isGifPaused = isGif && loaded && !showSkeleton && !isPlaying;

    return (
        <span
            className="image-with-skeleton-wrapper"
            style={{
                position: 'relative',
                display: 'block',
                width: '100%'
            }}
        >
            {showSkeleton && (
                <Skeleton
                    animation="wave"
                    variant="rectangular"
                    className={skeletonClassName || 'skeletonclass'}
                    sx={{
                        width: '100%',
                        height: 'auto',
                        ...skeletonSx
                    }}
                />
            )}
            <img
                ref={imgRef}
                className={className}
                alt={alt}
                src={src}
                onLoad={handleLoad}
                onError={handleError}
                loading='lazy'
                decoding="async"
                {...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
                style={showSkeleton
                    ? {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        pointerEvents: 'none'
                    }
                    : style}
            />
            {isGif && loaded && !showSkeleton && (
                <canvas
                    ref={canvasRef}
                    aria-hidden="true"
                    className={classNames('gif-freeze-frame', className)}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: canvasSize ? `${canvasSize.width}px` : '100%',
                        height: canvasSize ? `${canvasSize.height}px` : '100%',
                        ...(canvasSize && canvasSize.borderRadius ? { borderRadius: canvasSize.borderRadius } : {}),
                        opacity: isPlaying ? 0 : 1,
                        pointerEvents: 'none'
                    }}
                />
            )}
            <GifTag visible={isGifPaused} />
        </span>
    );
};

ImageWithSkeleton.propTypes = {
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    className: PropTypes.string,
    skeletonClassName: PropTypes.string,
    onLoad: PropTypes.func,
    style: PropTypes.object,
    skeletonSx: PropTypes.object,
    fetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
    isAnimated: PropTypes.bool
};

ImageWithSkeleton.defaultProps = {
    className: '',
    skeletonClassName: '',
    onLoad: undefined,
    style: undefined,
    skeletonSx: undefined,
    fetchPriority: undefined,
    isAnimated: undefined
};

export default ImageWithSkeleton;
